import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveErrorMessage, type ResolvedError } from '@kidshome/api-contract';
import { PIN_MIN_LENGTH, PIN_MAX_LENGTH } from '@kidshome/shared';
import { ModeToggle } from '@kidshome/ui';
import { useAuthActions, onLoggedIn, getKnownKids, type KnownKid } from '@/entities/session';
import { Avatar, LanguageToggle } from '@/shared/ui';
import { tDynamic } from '@/shared/lib/i18n';
import './kid-login.css';

/** First grapheme of a display name, upper-cased — the kid's initial for the photo-less fallback. */
function kidInitial(displayName: string): string {
  return [...displayName.trim()][0]?.toLocaleUpperCase() ?? '•';
}

/**
 * A soft, stable per-kid tint for the initials chip. Derived from the (UUIDv7) child id so the same
 * sibling always lands on the same hue across reloads, while different siblings reliably differ —
 * the whole point of the fallback. Pastel HSL pair keeps it on-theme without new tokens.
 */
function initialTint(id: string): React.CSSProperties {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  const hue = hash % 360;
  return {
    background: `linear-gradient(135deg, hsl(${hue} 72% 84%), hsl(${(hue + 28) % 360} 64% 72%))`,
  };
}

/**
 * Kid-login (FSD `pages`). Kid-friendly entry on a shared family device: pick your face ("Who's
 * playing?") then tap a 4–6 digit PIN. The kids come from this device's `known-kids` cache (populated
 * after a guardian signs in) — never from a public endpoint. Auth uses the shared `childLogin` action
 * (`POST /v1/auth/child-login`). No hardcoded strings (ADR-0025); API errors resolve via their code.
 */
export function KidLoginPage(): React.ReactElement {
  const { t } = useTranslation(['auth', 'common', 'settings', 'errors']);
  const navigate = useNavigate();
  const { childLogin } = useAuthActions();

  const known = useMemo(() => getKnownKids(), []);
  const [selected, setSelected] = useState<KnownKid | null>(null);
  const [pin, setPin] = useState('');
  const [submitError, setSubmitError] = useState<ResolvedError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // A brief, non-punitive "that's enough digits" pulse when a kid taps past PIN_MAX_LENGTH, so the
  // capped tap reads as a gentle full-up cue instead of a dead button (kind gamification).
  const [pinFull, setPinFull] = useState(false);

  const pick = (kid: KnownKid): void => {
    setSelected(kid);
    setPin('');
    setSubmitError(null);
    setPinFull(false);
  };

  // Every digit entry (pad or keyboard) updates the PIN and clears any stale wrong-PIN message,
  // so the red copy never lingers while the kid re-taps — matching the mockup's per-tap clear.
  const enterPin = useCallback((next: string): void => {
    setPin(next);
    setSubmitError(null);
  }, []);

  // The LATEST selection, readable after an await — a captured closure value can't detect a mid-flight
  // change (it only ever equals itself); this ref does. Updated during render so it always trails `selected`.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const submit = useCallback(async (): Promise<void> => {
    if (!known || !selected || pin.length < PIN_MIN_LENGTH || submitting) return;
    // Captured now so a later "Back" + pick of a different sibling can be detected once the await
    // below resolves — the in-flight request belongs to THIS kid only (see the staleness guard).
    const requestFor = selected;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await childLogin({
        householdId: known.householdId,
        childId: requestFor.id,
        pin,
      });
      // The screen may have moved on to a different sibling while this request was in flight (Back
      // is disabled while submitting, but guard anyway for belt-and-suspenders correctness). Only
      // the request's own kid may complete the login / claim its result.
      if (selectedRef.current?.id !== requestFor.id) return;
      onLoggedIn();
      void navigate('/', { replace: true });
    } catch (error) {
      if (selectedRef.current?.id !== requestFor.id) return;
      const resolved = resolveErrorMessage(error);
      // The child tapped a PIN — the generic "username or password" copy would be meaningless to
      // them. Re-voice the two expected failures in the warm childLogin tone; anything else keeps
      // the generic resolution.
      const kidKey =
        resolved.key === 'errors:INVALID_CREDENTIALS'
          ? 'auth:childLogin.wrongPin'
          : resolved.key === 'errors:ACCOUNT_LOCKED'
            ? 'auth:childLogin.locked'
            : null;
      setSubmitError(kidKey ? { key: kidKey, params: {} } : resolved);
      setPin('');
      setPinFull(false);
    } finally {
      // Always re-enable the screen once THIS request settles — the submitting flag disables Back/PinPad
      // for the whole screen, so it must clear regardless of whether the selection moved on (gating it
      // could otherwise strand the UI permanently disabled). Only result-claiming above is staleness-gated.
      setSubmitting(false);
    }
  }, [known, selected, pin, submitting, childLogin, navigate]);

  // Physical-keyboard entry for STATE B, listened at the document level (mirrors the mockup). A
  // div-level handler on the pinpad only fires once a key button has focus, so a keyboard/AT
  // user who starts typing on entry would get no response until they first tabbed in. Active only
  // while a kid is selected; Enter submits, 0–9 append (capped by PIN_MAX_LENGTH), Backspace deletes.
  useEffect(() => {
    if (!selected) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key >= '0' && e.key <= '9') {
        if (submitting) return;
        setPin((prev) => {
          if (prev.length >= PIN_MAX_LENGTH) {
            setPinFull(true);
            return prev;
          }
          return prev + e.key;
        });
        setSubmitError(null);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        if (submitting) return;
        setPin((prev) => prev.slice(0, -1));
      } else if (e.key === 'Enter') {
        // A focused native control (the "Parent? Sign in" link, Back, delete, …) already activates
        // itself on Enter — let that happen instead of hijacking the keypress into a PIN submit.
        const target = e.target;
        const ownsEnter =
          target instanceof HTMLElement && (target.tagName === 'A' || target.tagName === 'BUTTON');
        if (ownsEnter) return;
        e.preventDefault();
        void submit();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selected, submit, submitting]);

  // The full-up pulse is a one-shot cue — drop it shortly after it fires so the next over-tap can
  // re-trigger it. Tied to pinFull only, so it doesn't re-arm on unrelated re-renders.
  useEffect(() => {
    if (!pinFull) return;
    const id = window.setTimeout(() => setPinFull(false), 360);
    return () => window.clearTimeout(id);
  }, [pinFull]);

  const hasKids = !!known && known.kids.length > 0;

  // The grown-up sign-in link, pinned to the bottom of the scene. Mirrors the mockup's `.grown`
  // block (icon + label). Real navigation via <Link to="/login">.
  const grownLink = (
    <div className="grown">
      <Link to="/login">
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="8" r="4" fill="currentColor" />
          <path d="M4 20c0-4 4-6 8-6s8 2 8 6" fill="currentColor" />
        </svg>
        {t('auth:childLogin.parentSignIn')}
      </Link>
    </div>
  );

  return (
    <div className="kid kid-login-page">
      <div className="scene-inner flex flex-1 flex-col">
        {/* top bar: app name (with house mark) + language / theme toggles */}
        <div className="login-top">
          <span className="brand">
            <span className="mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
                <path d="M4 11l8-7 8 7v8a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1z" fill="#fff" />
              </svg>
            </span>
            {t('common:app.name')}
          </span>
          <span className="toggles">
            <LanguageToggle />
            <ModeToggle label={t('settings:appearance.toggleMode')} />
          </span>
        </div>

        {!selected ? (
          /* ============ STATE A: pick a kid ============ */
          <>
            <div className="greet">
              <h1>{t('auth:childLogin.whoIsPlaying')}</h1>
              <p>{t('auth:childLogin.title')}</p>
            </div>

            <div className="picker">
              {!hasKids ? (
                <div className="empty">
                  <span className="glyph" aria-hidden="true">
                    👋
                  </span>
                  <p>{t('auth:childLogin.noKidsYet')}</p>
                </div>
              ) : (
                <div className="kid-grid">
                  {known!.kids.map((kid) => (
                    <button
                      key={kid.id}
                      type="button"
                      className="kid-tile"
                      onClick={() => pick(kid)}
                      aria-label={kid.displayName}
                    >
                      <span className="kid-face-wrap">
                        {kid.avatar ? (
                          <Avatar avatar={kid.avatar} size="xl" />
                        ) : (
                          /* Uploaded-photo kids cache no preset key (ADR-0037 keeps the photo off
                             this pre-auth device list), so a bare <Avatar> would draw the same
                             default glyph for every one of them — two such siblings would be
                             indistinguishable. Fall back to the kid's tinted initial (as the
                             mockup does) so they still read apart without leaking the photo. */
                          <span
                            className="kid-initial"
                            style={initialTint(kid.id)}
                            aria-hidden="true"
                          >
                            {kidInitial(kid.displayName)}
                          </span>
                        )}
                        <span className="ring-spark" aria-hidden="true" />
                      </span>
                      <span className="nm">{kid.displayName}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {grownLink}
          </>
        ) : (
          /* ============ STATE B: PIN pad ============ */
          <>
            <div className="pinview">
              <div className="pin-head">
                <Avatar avatar={selected.avatar} size="lg" />
                <span className="who">{selected.displayName}</span>
                <span className="ask">{t('auth:childLogin.subtitle')}</span>
              </div>

              {/* dots fill 4–6 long (PIN_MIN_LENGTH 4, PIN_MAX_LENGTH 6); a brief full-up pulse on over-tap */}
              <div
                className={pinFull ? 'pin-dots full' : 'pin-dots'}
                role="status"
                aria-live="polite"
                aria-label={t('auth:childLogin.pinEntered', { count: pin.length })}
              >
                {Array.from({ length: Math.max(PIN_MIN_LENGTH, pin.length) }).map((_, i) => (
                  <span key={i} aria-hidden="true" className={i < pin.length ? 'dot on' : 'dot'} />
                ))}
              </div>

              <p className="pin-msg" role="alert">
                {submitError ? tDynamic(t, submitError.key, submitError.params) : ''}
              </p>

              <PinPad
                value={pin}
                max={PIN_MAX_LENGTH}
                onChange={enterPin}
                onAtMax={() => setPinFull(true)}
                disabled={submitting}
                deleteLabel={t('auth:childLogin.deleteDigit')}
              />

              <div className="pin-actions">
                <button
                  type="button"
                  className="btn btn-ghost flex-1"
                  disabled={submitting}
                  onClick={() => setSelected(null)}
                >
                  {t('auth:childLogin.back')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary flex-1 disabled:opacity-50"
                  disabled={pin.length < PIN_MIN_LENGTH || submitting}
                  onClick={() => void submit()}
                >
                  {t('auth:childLogin.submit')}
                </button>
              </div>
            </div>

            {grownLink}
          </>
        )}
      </div>
    </div>
  );
}

/** Kid-friendly numeric PIN pad — big tappable digits + delete. Each key is its own labelled button.
 * Physical-keyboard entry (0–9 / Backspace / Enter) is handled at the document level by the parent
 * while a kid is selected, so keyboard + AT users aren't forced to tab through all twelve buttons. */
function PinPad({
  value,
  max,
  onChange,
  onAtMax,
  disabled = false,
  deleteLabel,
}: {
  value: string;
  max: number;
  onChange: (next: string) => void;
  /** Fired when a digit is tapped while the PIN is already full, for the gentle full-up cue. */
  onAtMax: () => void;
  /** Lock the pad while a login is in flight, so taps don't queue against a cleared PIN. */
  disabled?: boolean;
  deleteLabel: string;
}): React.ReactElement {
  const press = (digit: string): void => {
    if (disabled) return;
    if (value.length < max) onChange(value + digit);
    else onAtMax();
  };
  return (
    <div className="pinpad">
      {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
        <button
          key={d}
          type="button"
          className="key"
          onClick={() => press(d)}
          disabled={disabled}
          aria-label={d}
        >
          {d}
        </button>
      ))}
      <span className="key ghost" aria-hidden="true" />
      <button
        type="button"
        className="key"
        onClick={() => press('0')}
        disabled={disabled}
        aria-label="0"
      >
        0
      </button>
      <button
        type="button"
        className="key del"
        onClick={() => onChange(value.slice(0, -1))}
        disabled={disabled || value.length === 0}
        aria-label={deleteLabel}
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M9 5h11v14H9l-6-7z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M12 10l5 5M17 10l-5 5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}
